// dragRef — the ONE wire format for dragging a Symbols entity (ticket,
// project, project page, file, note, …) from any surface into any drop
// target (2026-08-02 per Nika "make it possible to drag and drop tickets,
// projects, project pages, files (generic canvases contents) into chat and
// canvas promptarea").
//
// Lives in shared/ because BOTH ends cross package boundaries: sources are
// in tickets/ canvas/ files/ documents/, targets are in chat/ and canvas/.
// Packages must never import from each other (FA001) — every consumer goes
// through el.call('setDragRef', …) / el.call('readDragRef', …).
//
// WIRE FORMAT
// A drag carries the ref twice, on purpose:
//
//   application/x-symbols-ref  JSON {kind, id, title, href, meta?}
//     The rich payload. A custom MIME type is invisible to other apps and
//     is NOT readable during dragover (the DataTransfer protected mode
//     exposes only `types` until drop) — which is exactly why `types` is
//     what _hasDragRef tests.
//
//   text/plain                 the href (or title when there's no href)
//     The graceful degradation leg. Dropping onto a plain textarea, another
//     browser tab, or an external app yields a useful link instead of
//     nothing. Targets that understand the rich type ignore this.
//
// Deliberately NOT text/uri-list: Chrome treats a uri-list drop over an
// editable region as a navigation-ish paste and some targets then insert a
// duplicate link next to our own chip.

export const DRAG_REF_MIME = 'application/x-symbols-ref'

// Entity kinds a drop target may receive. Targets should degrade gracefully
// on an unknown kind (render the title, keep the href) rather than reject —
// new kinds get added here as sources are wired up, and an older cached
// bundle must not hard-fail on a newer kind.
// `record`, `doc`, `mail`, `canvas` joined 2026-09-07 (VaultOS v2 design §2):
// drag-ref `kind` === EntityRef `type`, so any entity a page can name is also
// a thing it can drag. The four new kinds are exactly the EntityRef types the
// old list did not already carry — `ticket`, `file`, `event` are shared by
// both vocabularies and keep their existing meaning. See functions/entityRef.js
// for the ref grammar and the type→icon map the chips render from.
export const DRAG_REF_KINDS = [
  'ticket',
  'project',
  'page',
  'file',
  'note',
  'kb',
  'event',
  'record',
  'doc',
  'mail',
  'canvas'
]

// Stamp a drag with an entity ref. Call from onDragstart:
//   onDragstart: (e, el, s) =>
//     el.call('setDragRef', e, { kind: 'ticket', id: s.id, title: s.title, href: `/tasks?ticket=${s.id}` })
//
// `meta` is a free-form bag for target-side rendering (icon, number, state,
// mimeType…). Keep it small — some platforms cap DataTransfer payload size.
export const setDragRef = (e, ref) => {
  // FA204 / frank trap — inlined (self-contained local) instead of the bare
  // module-scope `DRAG_REF_MIME` read; the export above stays live for
  // genuine external consumers (Composer.js, CanvasPromptTextarea.js).
  const DRAG_REF_MIME = 'application/x-symbols-ref'
  const dt = e && e.dataTransfer
  if (!dt || !ref || !ref.kind) return
  const payload = {
    kind: ref.kind,
    id: ref.id != null ? String(ref.id) : '',
    title: ref.title || '',
    href: ref.href || '',
    ...(ref.meta ? { meta: ref.meta } : {})
  }
  try {
    dt.setData(DRAG_REF_MIME, JSON.stringify(payload))
    dt.setData('text/plain', payload.href || payload.title)
  } catch (_) {
    // Some platforms reject setData outside a real dragstart — degrade to
    // no-drag rather than throwing out of the handler.
  }
  // NEVER clobber an effectAllowed the source already set. A card can be
  // draggable for two different reasons at once — TicketCard sets 'move'
  // for its own kanban column drop, and overwriting that with 'copyLink'
  // makes the browser REJECT the column drop entirely (a target whose
  // dropEffect is 'move' can't accept a copyLink-only drag). Widening to
  // 'all' keeps both the existing move target and our copy/link targets
  // working off one drag.
  dt.effectAllowed = !dt.effectAllowed || dt.effectAllowed === 'uninitialized' ? 'copyLink' : 'all'
}

// True when a drag in flight carries our ref. Safe to call from onDragover,
// where getData() is blocked by DataTransfer protected mode but `types` is
// always readable — this is the whole reason the check is types-based.
export const hasDragRef = (e) => {
  // FA204 / frank trap — inlined, see setDragRef above.
  const DRAG_REF_MIME = 'application/x-symbols-ref'
  const types = e && e.dataTransfer && e.dataTransfer.types
  return !!types && Array.prototype.indexOf.call(types, DRAG_REF_MIME) !== -1
}

// True when a drag carries OS files. Same protected-mode reasoning.
export const hasDragFiles = (e) => {
  const types = e && e.dataTransfer && e.dataTransfer.types
  return !!types && Array.prototype.indexOf.call(types, 'Files') !== -1
}

// Read the ref back on drop. Returns null when the drag isn't ours or the
// payload is malformed — callers should fall through to their file/text
// handling rather than treating null as an error.
export const readDragRef = (e) => {
  // FA204 / frank trap — inlined, see setDragRef above.
  const DRAG_REF_MIME = 'application/x-symbols-ref'
  const dt = e && e.dataTransfer
  if (!dt) return null
  let raw
  try {
    raw = dt.getData(DRAG_REF_MIME)
  } catch (_) {
    return null
  }
  if (!raw) return null
  try {
    const ref = JSON.parse(raw)
    return ref && ref.kind ? ref : null
  } catch (_) {
    return null
  }
}

// Human label for a ref chip — falls back through title → href → kind so a
// chip is never blank even when a source stamps a partial ref.
export const dragRefLabel = (ref) => (ref && (ref.title || ref.href || ref.kind)) || 'Reference'

// Icon name per kind, for the chip a target renders. Kinds not listed get
// the generic document glyph (see DRAG_REF_KINDS on why unknown must not
// hard-fail).
// `document`, not `file` — `file` is not a registered icon name in this design
// system (the whole checkout uses `document` 73× vs 2× for `file`, and the
// framework's own FileIcon carries the same wrong name). An unresolved name
// degrades to a blank glyph rather than throwing, so this would have shipped
// as a silently empty chip icon.
export const dragRefIcon = (ref) => {
  // FA204 / frank trap — inlined (self-contained local), see setDragRef above.
  const KIND_ICONS = {
    ticket: 'checkOval',
    project: 'grid',
    page: 'document',
    file: 'document',
    note: 'edit',
    kb: 'star',
    event: 'dateOutline'
  }
  return KIND_ICONS[ref && ref.kind] || 'document'
}

// The text a ref contributes when a target has to serialise it into a plain
// message/prompt body (chat markdown, AI prompt text). Markdown link when we
// have an href, bare title otherwise.
export const dragRefToText = (ref) => {
  if (!ref) return ''
  // FA204 / frank trap — self-contained inline copy of dragRefLabel (a bare
  // sibling-export read is exactly as unresolvable post-frank as a
  // module-local read — see notificationScope.js for the identical shape).
  const label = (ref && (ref.title || ref.href || ref.kind)) || 'Reference'
  return ref.href ? `[${label}](${ref.href})` : label
}

// ── Pointer-drag → ref-drop bridge ──────────────────────────────────────────
// Surfaces whose CUSTOM pointer-drag owns the gesture (the tickets board —
// see tickets/workspace.md "entity drag-out" ticket) cannot use native HTML5
// drag: dragstart starves pointermove and freezes the surface's own
// reposition interaction. Instead the pointer-drag's pointerup hit-tests for
// a declared drop target and DELIVERS the ref through the target's EXISTING
// native onDrop handler via a synthetic DragEvent — one wire format, zero
// duplicated attach logic — and the source treats the gesture as an ATTACH:
// it reverts its own move instead of committing it (2026-08-06 per Nika
// "task drag and drop in chat should not change position of task ticket -
// it should get attached ... and revert to its original position").
//
// Targets opt in by carrying `data-ref-drop` on the SAME element (or an
// ancestor of) their onDrop handler — the chat Composer and the canvas
// prompt textarea declare it.
export const REF_DROP_ATTR = 'data-ref-drop'

// The nearest declared ref-drop target at/above `node`, or null.
export const refDropTargetFrom = (node) => {
  // FA204 / frank trap — inlined, see setDragRef above.
  const REF_DROP_ATTR = 'data-ref-drop'
  return node && node.closest ? node.closest('[' + REF_DROP_ATTR + ']') : null
}

// Deliver `ref` to a declared target as a synthetic drop. Returns true when
// the event was dispatched (the target's own onDrop owns the attach from
// there). False when the environment can't construct DataTransfer/DragEvent
// — callers must STILL treat the gesture as consumed (revert, never fall
// through to a positional commit).
export const dispatchRefDrop = (targetNode, ref, pos) => {
  if (!targetNode || !ref || !ref.kind) return false
  // FA204 / frank trap — inlined, see setDragRef above.
  const DRAG_REF_MIME = 'application/x-symbols-ref'
  try {
    // `globalThis.DataTransfer` (not the bare global) — frank's known-globals
    // allowlist doesn't recognize `DataTransfer` by name (`DragEvent` right
    // below it is recognized), so the bare reference was an
    // `unresolvable-scope-read` `frank to-json` finding. Same runtime
    // behavior: throws (caught below, returns false) in any environment
    // without a real DataTransfer constructor, browser or not.
    const dt = new globalThis.DataTransfer()
    dt.setData(
      DRAG_REF_MIME,
      JSON.stringify({
        kind: ref.kind,
        id: ref.id != null ? String(ref.id) : '',
        title: ref.title || '',
        href: ref.href || '',
        ...(ref.meta ? { meta: ref.meta } : {})
      })
    )
    dt.setData('text/plain', ref.href || ref.title || '')
    const ev = new DragEvent('drop', {
      bubbles: true,
      cancelable: true,
      dataTransfer: dt,
      clientX: (pos && pos.clientX) || 0,
      clientY: (pos && pos.clientY) || 0
    })
    targetNode.dispatchEvent(ev)
    return true
  } catch (_) {
    return false
  }
}
